import React, { useState, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Inbox, Search, MessageSquare, Send, CheckCircle, RotateCcw,
  ChevronRight, User, Mail,
} from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import { useUserStore } from '@/lib/user-store';
import { useTranslation } from '@/hooks/useTranslation';
import toast from 'react-hot-toast';

type StatusFilter = 'all' | 'open' | 'resolved';

const AdminInboxPage: React.FC = () => {
  const { t } = useTranslation();
  const conversations = useAdminStore((s) => s.conversations);
  const addMessageToConversation = useAdminStore((s) => s.addMessageToConversation);
  const markConversationResolved = useAdminStore((s) => s.markConversationResolved);
  const markConversationOpen = useAdminStore((s) => s.markConversationOpen);
  const markMessagesRead = useAdminStore((s) => s.markMessagesRead);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [replyText, setReplyText] = useState('');
  const threadEndRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    let items = [...(conversations ?? [])].sort((a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime());
    if (statusFilter !== 'all') items = items.filter((c) => c.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      items = items.filter((c) =>
        c.subject.toLowerCase().includes(q) ||
        (c.guestName || '').toLowerCase().includes(q) ||
        (c.messages ?? []).some((m) => m.senderName.toLowerCase().includes(q) || m.content.toLowerCase().includes(q))
      );
    }
    return items;
  }, [conversations, statusFilter, search]);

  const openCount = (conversations ?? []).filter((c) => c.status === 'open').length;
  const resolvedCount = (conversations ?? []).filter((c) => c.status === 'resolved').length;

  // Scroll to bottom of thread when expanding or replying
  useEffect(() => {
    if (expandedId) threadEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [expandedId, conversations]);

  const handleExpand = (convId: string): void => {
    if (expandedId === convId) {
      setExpandedId(null);
      setReplyText('');
    } else {
      setExpandedId(convId);
      setReplyText('');
      // Mark user/guest messages as read (admin is reading)
      markMessagesRead(convId, 'admin');
    }
  };

  const handleReply = (convId: string): void => {
    if (!replyText.trim()) return;
    const conv = (conversations ?? []).find((c) => c._id === convId);
    if (!conv) return;

    addMessageToConversation(convId, {
      conversationId: convId,
      senderType: 'admin',
      senderName: 'Admin',
      content: replyText.trim(),
      read: false,
    });

    // Notifications now come from API conversations (unread admin messages)
    // No need for localStorage-based notification push

    setReplyText('');
    toast.success(t.inbox.messageSent);
  };

  const users = useUserStore((s) => s.getUsers());

  const getSenderDisplay = (conv: typeof conversations[0]): { name: string; email: string } => {
    // Look up user by userId if available
    if (conv.userId) {
      const user = users.find((u) => u._id === conv.userId);
      if (user) {
        return {
          name: `${user.firstName} ${user.lastName}`,
          email: user.email,
        };
      }
    }
    const firstMsg = conv.messages?.[0];
    return {
      name: conv.guestName || firstMsg?.senderName || 'Unknown',
      email: conv.guestEmail || firstMsg?.senderEmail || '',
    };
  };

  const formatDate = (date: string): string =>
    new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const formatTime = (date: string): string =>
    new Date(date).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-syncopate font-bold tracking-tighter">{t.inbox.title}</h1>
          <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.3em] text-black/20 dark:text-white/20">
            {(conversations ?? []).length} total
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/20 dark:text-white/20" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search conversations..."
            className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-black/[0.06] dark:border-white/[0.06] bg-transparent text-[11px] placeholder:text-black/25 dark:placeholder:text-white/25 focus:outline-none focus:ring-2 focus:ring-black/10 dark:focus:ring-white/10"
          />
        </div>
        <div className="flex gap-1">
          {([
            { id: 'all' as StatusFilter, label: 'All', count: (conversations ?? []).length },
            { id: 'open' as StatusFilter, label: t.inbox.open, count: openCount },
            { id: 'resolved' as StatusFilter, label: t.inbox.resolved, count: resolvedCount },
          ]).map((s) => (
            <button
              key={s.id}
              onClick={() => setStatusFilter(s.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-[9px] font-bold uppercase tracking-[0.1em] rounded-lg transition-all ${
                statusFilter === s.id
                  ? 'bg-black dark:bg-white text-white dark:text-black'
                  : 'text-black/30 dark:text-white/30 hover:text-black/60 dark:hover:text-white/60'
              }`}
            >
              {s.id === 'open' && <span className="w-1.5 h-1.5 rounded-full bg-green-500" />}
              {s.id === 'resolved' && <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />}
              {s.label}
              <span className="opacity-50">({s.count})</span>
            </button>
          ))}
        </div>
      </div>

      {/* Conversations */}
      {filtered.length > 0 ? (
        <div className="space-y-3">
          {filtered.map((conv) => {
            const isExpanded = expandedId === conv._id;
            const sender = getSenderDisplay(conv);
            const unreadCount = (conv.messages ?? []).filter((m) => !m.read && m.senderType !== 'admin').length;

            return (
              <motion.div
                key={conv._id}
                layout
                className="bg-white dark:bg-black border border-black/[0.04] dark:border-white/[0.04] rounded-2xl overflow-hidden"
              >
                {/* Summary row */}
                <button
                  onClick={() => handleExpand(conv._id)}
                  className="w-full flex items-center gap-4 p-4 sm:p-5 text-left hover:bg-black/[0.02] dark:hover:bg-white/[0.02] transition-colors"
                >
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                    conv.status === 'open' ? 'bg-green-500/10 text-green-500' : 'bg-black/[0.04] dark:bg-white/[0.04] text-black/30 dark:text-white/30'
                  }`}>
                    <MessageSquare size={18} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-bold truncate">{conv.subject}</p>
                      {unreadCount > 0 && (
                        <span className="w-5 h-5 rounded-full bg-blue-500 text-white text-[8px] font-bold flex items-center justify-center shrink-0">
                          {unreadCount}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <User size={10} className="text-black/25 dark:text-white/25 shrink-0" />
                      <span className="text-[10px] text-black/40 dark:text-white/40 truncate">{sender.name}</span>
                      {conv.source === 'contact' && (
                        <span className="px-1.5 py-0.5 text-[7px] font-bold uppercase tracking-wider bg-orange-500/10 text-orange-500 rounded">Contact</span>
                      )}
                      {conv.source === 'profile' && (
                        <span className="px-1.5 py-0.5 text-[7px] font-bold uppercase tracking-wider bg-blue-500/10 text-blue-500 rounded">Message</span>
                      )}
                      {conv.source === 'custom-frame' && (
                        <span className="px-1.5 py-0.5 text-[7px] font-bold uppercase tracking-wider bg-purple-500/10 text-purple-500 rounded">Frame</span>
                      )}
                      {!conv.source && conv.userId && (
                        <span className="px-1.5 py-0.5 text-[7px] font-bold uppercase tracking-wider bg-blue-500/10 text-blue-500 rounded">User</span>
                      )}
                      {sender.email && (
                        <>
                          <Mail size={10} className="text-black/25 dark:text-white/25 shrink-0" />
                          <span className="text-[10px] text-black/30 dark:text-white/30 truncate">{sender.email}</span>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="text-right shrink-0 space-y-1">
                    <span className={`inline-block px-2.5 py-0.5 rounded-full text-[8px] font-bold uppercase tracking-wider ${
                      conv.status === 'open' ? 'bg-green-500/10 text-green-500' : 'bg-black/[0.06] dark:bg-white/[0.06] text-black/30 dark:text-white/30'
                    }`}>
                      {conv.status === 'open' ? t.inbox.open : t.inbox.resolved}
                    </span>
                    <p className="text-[9px] text-black/25 dark:text-white/25">
                      {(conv.messages ?? []).length} msg · {formatDate(conv.lastMessageAt)}
                    </p>
                  </div>

                  <ChevronRight size={16} className={`text-black/20 dark:text-white/20 transition-transform shrink-0 ${isExpanded ? 'rotate-90' : ''}`} />
                </button>

                {/* Expanded thread */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      {/* Action bar */}
                      <div className="flex items-center gap-2 px-5 py-3 border-t border-black/[0.04] dark:border-white/[0.04] bg-black/[0.01] dark:bg-white/[0.01]">
                        {conv.status === 'open' ? (
                          <button
                            onClick={() => { markConversationResolved(conv._id); toast.success(t.inbox.resolved); }}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-[9px] font-bold uppercase tracking-[0.1em] text-green-600 dark:text-green-400 bg-green-500/10 rounded-lg hover:bg-green-500/20 transition-colors"
                          >
                            <CheckCircle size={12} /> {t.inbox.markResolved}
                          </button>
                        ) : (
                          <button
                            onClick={() => { markConversationOpen(conv._id); toast.success(t.inbox.open); }}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-[9px] font-bold uppercase tracking-[0.1em] text-blue-600 dark:text-blue-400 bg-blue-500/10 rounded-lg hover:bg-blue-500/20 transition-colors"
                          >
                            <RotateCcw size={12} /> {t.inbox.reopen}
                          </button>
                        )}
                        {conv.userId && (
                          <span className="text-[9px] text-black/25 dark:text-white/25 ml-auto">
                            User ID: {conv.userId.slice(0, 8)}
                          </span>
                        )}
                      </div>

                      {/* Messages */}
                      <div className="border-t border-black/[0.04] dark:border-white/[0.04] p-4 sm:p-5 space-y-3 max-h-[500px] overflow-y-auto">
                        {(conv.messages ?? []).map((msg) => (
                          <div
                            key={msg._id}
                            className={`flex ${msg.senderType === 'admin' ? 'justify-end' : 'justify-start'}`}
                          >
                            <div className={`max-w-[75%] p-3.5 rounded-2xl ${
                              msg.senderType === 'admin'
                                ? 'bg-black dark:bg-white text-white dark:text-black rounded-tr-sm'
                                : 'bg-black/[0.04] dark:bg-white/[0.06] rounded-tl-sm'
                            }`}>
                              <div className={`flex items-center gap-2 mb-1 ${
                                msg.senderType === 'admin' ? 'text-white/60 dark:text-black/60' : 'text-black/40 dark:text-white/40'
                              }`}>
                                <span className="text-[9px] font-bold uppercase tracking-wider">{msg.senderName}</span>
                                {msg.senderEmail && <span className="text-[8px] opacity-60">{msg.senderEmail}</span>}
                              </div>
                              <p className="text-[13px] leading-relaxed whitespace-pre-wrap">{msg.content.split(/(https?:\/\/[^\s]+)/g).map((part, pi) =>
                                /^https?:\/\//.test(part) ? <a key={pi} href={part} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline break-all">{part}</a> : part
                              )}</p>
                              <p className={`text-[8px] mt-2 ${
                                msg.senderType === 'admin' ? 'text-white/30 dark:text-black/30' : 'text-black/20 dark:text-white/20'
                              }`}>
                                {formatTime(msg.createdAt)}
                              </p>
                            </div>
                          </div>
                        ))}
                        <div ref={threadEndRef} />
                      </div>

                      {/* Reply form */}
                      <div className="border-t border-black/[0.04] dark:border-white/[0.04] p-4 sm:p-5">
                        <div className="flex gap-3">
                          <textarea
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder={t.inbox.replyPlaceholder}
                            rows={2}
                            className="flex-1 px-4 py-3 rounded-xl border border-black/[0.08] dark:border-white/[0.08] bg-transparent text-sm placeholder:text-black/25 dark:placeholder:text-white/25 focus:outline-none focus:ring-2 focus:ring-black/10 dark:focus:ring-white/10 resize-none"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleReply(conv._id);
                              }
                            }}
                          />
                          <button
                            onClick={() => handleReply(conv._id)}
                            disabled={!replyText.trim()}
                            className="self-end px-5 py-3 bg-black dark:bg-white text-white dark:text-black rounded-xl hover:bg-black/90 dark:hover:bg-white/90 disabled:opacity-30 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                          >
                            <Send size={14} />
                            <span className="text-[10px] font-bold uppercase tracking-widest hidden sm:inline">{t.inbox.send}</span>
                          </button>
                        </div>
                        <p className="text-[8px] text-black/20 dark:text-white/20 mt-2">Enter to send · Shift+Enter for new line</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-2xl bg-black/[0.03] dark:bg-white/[0.03] flex items-center justify-center mx-auto mb-4">
            <Inbox size={24} className="text-black/10 dark:text-white/10" />
          </div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/20 dark:text-white/20">
            {(conversations ?? []).length === 0 ? t.inbox.noMessages : 'No conversations match this filter'}
          </p>
        </div>
      )}
    </div>
  );
};

export default AdminInboxPage;
